import type { ApplicationSpendDto, ClientDashboardDto } from '@botguide/shared';
import { formatCents, formatRatio } from '@botguide/shared';

import { Badge, Card, Icon } from '@/components';
import { cn } from '@/lib/cn';
import { EMPTY, formatCount } from '@/lib/format';

import { LaneSummary } from './LaneSummary';

type DashboardApplication = ClientDashboardDto['applications'][number];

export interface ApplicationCardProps {
  entry: DashboardApplication;
  /** Staggers the page-load reveal. One entrance, not twenty. */
  index?: number;
  className?: string;
}

/**
 * One application: what it is, what it cost, what it is projected to return,
 * and everything currently being built inside it.
 *
 * This component renders money, so a rule from the requirements lives here
 * rather than in the page:
 *
 *  - A null ratio renders as an em dash with the reason, never as `NaN%` or
 *    `Infinity%`. `projectedRoi` returns null precisely so the UI is forced
 *    to say something true instead.
 *
 * ## The cutoff date, removed 2026-10-16
 *
 * This file used to print "based on spend through <date>" beside every ROI
 * figure, and the rule above it said why: the ratio divides a forward-looking
 * benefit by spend-to-date, so it falls every month as costs accumulate, and
 * without the cutoff a client reads a falling number as the project getting
 * worse.
 *
 * Removed at Ryan's request. The reasoning was put to him first and is kept
 * here rather than deleted, because the failure it describes is still real:
 * if a client ever asks why a return figure went down, this is the answer,
 * and the line that used to supply it is gone.
 */
export function ApplicationCard({ entry, index = 0, className }: ApplicationCardProps) {
  const { application, projects, features, lanes, spend } = entry;
  const liveProjects = projects.filter((project) => !project.isArchived);

  return (
    <Card
      padding="lg"
      className={cn('rise', className)}
      style={{ animationDelay: `${Math.min(index, 6) * 70}ms` }}
    >
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-subtle">
            Application
          </p>
          <h2 className="mt-1 font-display text-[1.375rem] font-semibold tracking-[-0.015em] sm:text-[1.5rem]">
            {application.name}
          </h2>
          {application.summary ? (
            <p className="mt-1.5 max-w-prose text-sm leading-relaxed text-muted">
              {application.summary}
            </p>
          ) : null}
        </div>

        {application.productionUrl ? (
          <a
            href={application.productionUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line-strong px-3 py-1.5 text-[0.8125rem] font-medium text-ink transition-colors hover:border-accent hover:text-accent-ink"
          >
            Open it
            <Icon name="external" size={13} />
          </a>
        ) : null}
      </header>

      <ValueStrip spend={spend} className="mt-5" />

      <div className="mt-6 border-t border-line pt-5">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-subtle">
            What is being built
          </h3>
          <p className="font-mono text-[0.75rem] text-subtle tnum">
            {formatCount(features.length, 'feature')} · {formatCount(liveProjects.length, 'engagement')}
          </p>
        </div>

        <LaneSummary lanes={lanes} features={features} />
      </div>

      {/*
        The engagement list that used to sit here was removed on 2026-10-16.
        It wrapped a dozen project names and date ranges into a dense block at
        the foot of every card - including entries like "Access migration ·
        — – May 27", where a project has no start date and the dash renders
        against nothing. The count the client needs is already in the heading
        above ("12 features · 11 engagements"); the names were detail nobody
        was reading.
      */}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Money                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Why a ratio is missing, said in the client's terms.
 *
 * `projectedRoi` returns null for two different reasons and they need two
 * different sentences: nothing has been invoiced yet, or nobody has recorded
 * what this is expected to be worth. Collapsing them into "not available"
 * would leave a reader unable to tell which one to act on.
 */
/**
 * One figure, never a range.
 *
 * `application_benefits` stores a low and a high, and this card used to print
 * both. Every figure recorded on 2026-10-16 is a point estimate - low and
 * high hold the same cents - so that produced "$300,000.00 - $300,000.00",
 * and "+2163% to +2163%" in the ratio beside it.
 *
 * Ryan's call on 2026-10-16: one amount and one percentage, always. So when a
 * genuine bracket is ever recorded again - Time Tracker's $45,000-$60,000 was
 * one, before it was superseded - this shows its LOW end. Understating a
 * projection to the client paying for the work is the safe direction to be
 * wrong in, and the stored high is still there for anyone who queries it.
 */
function benefitValue(spend: ApplicationSpendDto): number | null {
  return spend.benefitLowCents ?? spend.benefitHighCents;
}

/** The ratio shown, matching `benefitValue`'s conservative end. */
function roiValue(spend: ApplicationSpendDto): number | null {
  return spend.roiLowRatio ?? spend.roiHighRatio;
}

function missingRoiReason(spend: ApplicationSpendDto): string {
  if (spend.spendCents === 0) {
    return 'Nothing has been invoiced against this application yet, so there is no cost to measure a return against.';
  }
  return 'We have not recorded an expected benefit for this application yet.';
}

/**
 * The colour of the return figure, by sign.
 *
 * A negative return rendered in the "good" green is worse than no colour at
 * all: it is the one figure on this screen a reader takes at a glance, and
 * East Hollow's return is genuinely negative in the seed.
 *
 * This used to take a low and a high and go amber when the range straddled
 * break-even, because "it depends" was a real answer when a bracket could sit
 * either side of zero. Since 2026-10-16 the card shows one figure, so there
 * is no straddling case left and the amber branch would have been unreachable
 * code pretending to handle a state that cannot occur.
 */
function returnTone(value: number | null): string {
  if (value === null) return 'var(--ink-muted)';
  return value >= 0 ? 'var(--ok)' : 'var(--danger)';
}

function ValueStrip({ spend, className }: { spend: ApplicationSpendDto | null; className?: string }) {
  if (!spend) {
    return (
      <p
        className={cn(
          'rounded-md border border-dashed border-line-strong bg-sunken/60 px-4 py-3 text-[0.8125rem] text-muted',
          className,
        )}
      >
        Spend for this application has not been published yet.
      </p>
    );
  }

  const hasRange = spend.roiLowRatio !== null || spend.roiHighRatio !== null;
  const hasBenefit = spend.benefitLowCents !== null || spend.benefitHighCents !== null;

  return (
    <section
      className={cn('rounded-lg border border-line bg-sunken/70 px-4 py-4 sm:px-5', className)}
      aria-label={`Spend and projected return for ${spend.applicationName}`}
    >
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-8">
        <div className="min-w-0">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-subtle">
            Invested to date
          </p>
          <p className="mt-1.5 font-display text-[2rem] leading-none tracking-tight tnum">
            {formatCents(spend.spendCents)}
          </p>
          {hasBenefit ? (
            <p className="mt-2 text-[0.8125rem] text-muted">
              Expected to be worth{' '}
              <span className="font-mono tnum">
                {benefitValue(spend) === null ? EMPTY : formatCents(benefitValue(spend) ?? 0)}
              </span>
            </p>
          ) : null}
        </div>

        <div className="min-w-0 border-t border-line pt-4 sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-subtle">
            Projected return
          </p>

          {hasRange ? (
            <>
              {/*
                "to" rather than an en dash: half the seeded ranges are
                negative, and "-67%--40%" is unreadable however it is spaced.
              */}
              <p
                className="mt-1.5 font-display text-[2rem] leading-none tracking-tight tnum"
                style={{ color: returnTone(roiValue(spend)) }}
              >
                {formatRatio(roiValue(spend))}
              </p>
            </>
          ) : (
            <>
              <p className="mt-1.5 font-display text-[2rem] leading-none tracking-tight text-subtle">
                {EMPTY}
              </p>
              <p className="mt-2 max-w-prose text-[0.8125rem] text-muted">
                {missingRoiReason(spend)}
              </p>
            </>
          )}
        </div>
      </div>

      {hasRange ? (
        <p className="mt-4 flex items-start gap-2 border-t border-line pt-3 text-[0.75rem] leading-relaxed text-subtle">
          <Icon name="info" size={13} className="mt-0.5 shrink-0" />
          <span>Expected Annual Benefit / Invoiced.</span>
        </p>
      ) : null}

      {spend.spendCents === 0 ? (
        <Badge tone="neutral" className="mt-4">
          No invoices allocated yet
        </Badge>
      ) : null}
    </section>
  );
}
